import React, { useEffect, useState } from "react";
import {
  Box,
  Checkbox,
  Typography,
  IconButton,
  Tooltip,
  CircularProgress,
} from "@mui/material";
import SettingsVoiceIcon from "@mui/icons-material/SettingsVoice";
import ImageIcon from "@mui/icons-material/Image";
import { THEME_COLORS } from "../../../context/constants";
import { AssetActor, CustomVoice, HeygenVoice } from "../../../context/types";
import {
  getCustomVoicesByWorkspace,
  listAllHeygenVoices,
  updateAssetActor,
} from "../../../client/api";
import VoiceSelectionModal from "../../VoicePlayer/components/HeygenVoicesDialog";
import { useNotification } from "../../../context/NotificationContext";
import { useUserContext } from "../../../context/UserContext";

interface AssetActorCardProps {
  assetActor: AssetActor;
  isSelected: boolean;
  onSelect: (id: string, isCheckboxClick?: boolean) => void;
  onUpdate: (updated: AssetActor) => void;
  scriptType: "text" | "speech";
}

const AssetActorCard: React.FC<AssetActorCardProps> = ({
  assetActor,
  isSelected,
  onSelect,
  onUpdate,
  scriptType,
}) => {
  const { addNotification } = useNotification();
  const { currentWorkspace } = useUserContext();
  const [voiceDialogOpen, setVoiceDialogOpen] = useState(false);
  const [heygenVoices, setHeygenVoices] = useState<HeygenVoice[]>([]);
  const [heygenCustomVoices, setHeygenCustomVoices] = useState<CustomVoice[]>([]);
  const [heygenCustomVoicesLoading, setHeygenCustomVoicesLoading] = useState(false);
  const [loadingVoices, setLoadingVoices] = useState(false);

  const imageUrl = assetActor.image_url || "";

  const fetchHeygenCustomVoices = async () => {
    if (!currentWorkspace?._id) return;
    try {
      setHeygenCustomVoicesLoading(true);
      const response = await getCustomVoicesByWorkspace(currentWorkspace._id);
      setHeygenCustomVoices(response.data || []);
    } catch (error) {
      console.error("Error fetching custom Heygen voices:", error);
    } finally {
      setHeygenCustomVoicesLoading(false);
    }
  };

  useEffect(() => {
    fetchHeygenCustomVoices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentWorkspace?._id]);

  const handleOpenVoiceDialog = async () => {
    setVoiceDialogOpen(true);
    if (heygenVoices.length === 0) {
      setLoadingVoices(true);
      try {
        const response = await listAllHeygenVoices();
        setHeygenVoices(response.data);
      } catch (err) {
        addNotification("Failed to load voices", "error");
      } finally {
        setLoadingVoices(false);
      }
    }
  };

  const handleSelectVoice = async (
    voiceId: string,
    meta?: { name: string; preview_audio_url: string | null }
  ) => {
    try {
      const libraryMatch = heygenVoices.find((v) => v.voice_id === voiceId);
      const customMatch = heygenCustomVoices.find((v) => v.voice_id === voiceId);
      const resolvedName =
        libraryMatch?.name || customMatch?.name || meta?.name || voiceId;
      const updated = await updateAssetActor(assetActor._id, {
        voice_id: voiceId,
        voiceName: resolvedName,
      });
      onUpdate(updated);
      setVoiceDialogOpen(false);
      addNotification(`Voice set to ${resolvedName}`, "success");
    } catch (err) {
      addNotification("Failed to update voice", "error");
    }
  };

  return (
    <>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          width: "100%",
          cursor: "pointer",
        }}
        onClick={() => onSelect(assetActor._id)}
      >
        {/* Checkbox */}
        <Checkbox
          checked={isSelected}
          onClick={(e) => {
            e.stopPropagation();
            onSelect(assetActor._id, true);
          }}
          sx={{
            p: 0,
            "& svg": { fill: THEME_COLORS.yellow },
          }}
        />

        {/* Thumbnail */}
        <Box
          sx={{
            width: "48px",
            height: "48px",
            borderRadius: "50%",
            overflow: "hidden",
            flexShrink: 0,
            backgroundColor: "#f0f0f0",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            border: isSelected ? `2px solid ${THEME_COLORS.yellow}` : "2px solid transparent",
          }}
        >
          {imageUrl ? (
            <img
              src={imageUrl}
              alt={assetActor.name}
              style={{ width: "100%", height: "100%", objectFit: "cover" }}
            />
          ) : (
            <ImageIcon sx={{ fontSize: 24, color: "#ccc" }} />
          )}
        </Box>

        {/* Info */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 600,
                fontFamily: "Lexend, sans-serif",
                fontSize: "13px",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {assetActor.name}
            </Typography>
            <Box
              sx={{
                backgroundColor: "#00897B",
                color: "#fff",
                fontSize: "9px",
                fontWeight: "bold",
                padding: "1px 5px",
                borderRadius: "4px",
                flexShrink: 0,
              }}
            >
              Image
            </Box>
          </Box>

          {/* Voice row */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.25 }}>
            <Tooltip title="Select Voice">
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleOpenVoiceDialog();
                }}
                sx={{ color: THEME_COLORS.yellow, p: 0.25 }}
              >
                {loadingVoices ? (
                  <CircularProgress size={14} sx={{ color: THEME_COLORS.yellow }} />
                ) : (
                  <SettingsVoiceIcon sx={{ fontSize: 16 }} />
                )}
              </IconButton>
            </Tooltip>
            <Typography
              variant="caption"
              sx={{
                color: assetActor.voiceName ? "#333" : "#aaa",
                fontFamily: "Lexend, sans-serif",
                fontSize: "11px",
                cursor: "pointer",
              }}
              onClick={(e) => {
                e.stopPropagation();
                handleOpenVoiceDialog();
              }}
            >
              {assetActor.voiceName || "Select voice"}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Voice selection dialog */}
      <VoiceSelectionModal
        open={voiceDialogOpen}
        onClose={() => setVoiceDialogOpen(false)}
        heygenVoices={heygenVoices}
        onSelectVoice={handleSelectVoice}
        customVoices={heygenCustomVoices}
        customVoicesLoading={heygenCustomVoicesLoading}
        onRefreshCustomVoices={fetchHeygenCustomVoices}
      />
    </>
  );
};

export default AssetActorCard;
